/**
 * Firmware OTA control-plane client — `/v1/admin/firmware/*` (platform-admin).
 *
 * Releases are self-hosted (bytes in Postgres, no object store). A platform
 * admin uploads a signed build, rolls it out to all/selected claimed gateways,
 * and reads back per-device running version + OTA acknowledgement. Rollback =
 * roll a PRIOR release out again.
 */

import { api } from "@/lib/api-client";

export interface FirmwareRelease {
  id: string;
  version: string;
  channel: string;
  notes: string;
  size_bytes: number;
  sha256: string;
  min_kernel: string;
  filename: string;
  created_by: string;
  created_at: string;
  has_signature: boolean;
  devices_running: number;
}

export interface FirmwareDevice {
  thing_id: string;
  name: string;
  org_id: string;
  running_version: string | null;
  last_ota_state: string | null;
  last_ota_detail: string | null;
  last_ota_version: string | null;
  last_ota_at: string | null;
}

export interface RolloutResult {
  release_id: string;
  version: string;
  targeted: number;
  offered: number;
  results: Array<{ thing_id: string; state: string }>;
}

export interface UploadReleaseInput {
  version: string;
  channel?: string;
  notes?: string;
  min_kernel?: string;
  ed25519_sig?: string;
  file: File;
}

export const firmware = {
  listReleases: () =>
    api.get<{ releases: FirmwareRelease[]; total: number }>("/v1/admin/firmware/releases"),
  listDevices: () =>
    api.get<{ devices: FirmwareDevice[]; total: number }>("/v1/admin/firmware/devices"),
  upload: (input: UploadReleaseInput) => {
    const fd = new FormData();
    fd.append("version", input.version);
    if (input.channel) fd.append("channel", input.channel);
    if (input.notes) fd.append("notes", input.notes);
    if (input.min_kernel) fd.append("min_kernel", input.min_kernel);
    if (input.ed25519_sig) fd.append("ed25519_sig", input.ed25519_sig);
    fd.append("artifact", input.file);
    return api.postForm<{ id: string; version: string; size_bytes: number; sha256: string }>(
      "/v1/admin/firmware/releases",
      fd,
    );
  },
  /** Roll a release out to all claimed gateways, or a selected set. Rollback = roll a prior release out again. */
  rollout: (releaseId: string, body: { thing_ids?: string[]; all?: boolean }) =>
    api.post<RolloutResult>(`/v1/admin/firmware/releases/${releaseId}/rollout`, body),
  remove: (releaseId: string) => api.delete<void>(`/v1/admin/firmware/releases/${releaseId}`),
};
